import { Heading, HeadingLevel } from "@ariakit/react";
import { AsyncOp } from "@indietabletop/appkit/async-op";
import { useReducer } from "react";
import { config } from "../../config";
import { Army } from "../../domain/army";
import { Unit } from "../../domain/unit";
import { formatCombat } from "../../lib/format-combat";
import { ToggleField } from "../checkbox-field";
import { btn } from "../dialog-buttons/style.css";
import { KnownErrorView } from "../error-view";
import { useArmyQuery } from "../hooks/use-army-query";
import { useEnsureShareLink } from "../hooks/use-ensure-share-link";
import { Icon } from "../icon";
import { IconButton } from "../icon-button";
import { LoadingIndicator } from "../loading-indicator";
import { LoadingView } from "../loading-view";
import { useDocumentTheme } from "../theme/use-document-theme";
import { page, panel, printable, rules, table } from "./style.css";

type PrintSettings = {
  showRules: boolean;
  showDescription: boolean;
};

function reducer(state: PrintSettings, action: Partial<PrintSettings>) {
  return { ...state, ...action };
}

function usePrintSettings() {
  return useReducer(reducer, {
    showRules: true,
    showDescription: true,
  });
}

function QRCode(props: { shareLinkOp: AsyncOp<string, string> }) {
  const { shareLinkOp } = props;

  if (shareLinkOp.isPending) {
    return (
      <div className={printable.qrPlaceholder}>
        <LoadingIndicator />
      </div>
    );
  }

  if (shareLinkOp.isFailure) {
    return <div className={printable.qrPlaceholder}>QR code not available</div>;
  }

  return (
    <img
      src={`${config.ITC_API_ORIGIN}/qr?${new URLSearchParams({
        url: shareLinkOp.value,
      })}`}
      className={printable.qrImage}
      alt=""
    />
  );
}

function ArmyHeader(props: {
  army: Army;
  settings: PrintSettings;
  shareLinkOp: AsyncOp<string, string>;
}) {
  const { army, settings, shareLinkOp } = props;

  return (
    <div className={printable.header}>
      <div>
        <Heading className={printable.title}>{army.name}</Heading>

        <p className={printable.summary}>
          {army.summary} · Eternøl v{army.ruleset.version}
        </p>

        {army.keyword && (
          <p className={printable.summary}>Army keyword: {army.keyword.name}</p>
        )}

        {settings.showDescription && army.data.description && (
          <p className={printable.description}>{army.data.description}</p>
        )}
      </div>

      <div className={printable.qr}>
        <QRCode shareLinkOp={shareLinkOp} />
        Scan to load in the app
      </div>
    </div>
  );
}

/**
 * Profile name (if the unit was renamed) followed by keyword and ability names.
 */
function unitDetails(unit: Unit) {
  const names = [
    ...unit.keywords.map((k) => `${k.name}${k.ref.count > 1 ? " x2" : ""}`),
    ...unit.abilities.map((a) => a.name),
  ].join(", ");

  const profile = unit.name !== unit.profile.name ? unit.profile.name : null;

  return [profile, names].filter((part) => !!part).join(" · ");
}

function UnitsTable(props: { army: Army }) {
  const { army } = props;

  if (army.units.length === 0) {
    return <p className={printable.empty}>This army has no units yet.</p>;
  }

  return (
    <table className={table.table}>
      <thead>
        <tr className={table.headRow}>
          <th className={table.head}>Unit</th>
          <th className={table.head}>Range</th>
          <th className={table.head}>Move</th>
          <th className={table.head}>Combat</th>
          <th className={table.head}>Defense</th>
          <th className={table.head}>Morale</th>
          <th className={table.head}>Cost</th>
        </tr>
      </thead>

      <tbody>
        {army.units.map((unit) => {
          const details = unitDetails(unit);

          return (
            <tr key={unit.data.id} className={table.row}>
              <td className={table.cell}>
                <span className={table.unitName}>
                  {unit.name}
                  {unit.data.count > 1 && (
                    <span className={table.count}> x{unit.data.count}</span>
                  )}
                </span>

                {!unit.isValid && (
                  <span className={table.invalid}>Invalid</span>
                )}

                {details && <div className={table.unitDetails}>{details}</div>}
              </td>
              <td className={table.cell}>{unit.range}”</td>
              <td className={table.cell}>{unit.movement}”</td>
              <td className={table.cell}>{formatCombat(unit)}</td>
              <td className={table.cell}>{unit.defense}+</td>
              <td className={table.cell}>{unit.morale}</td>
              <td className={table.cell}>{unit.cost}pts</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function RulesReference(props: { army: Army }) {
  const { rules: armyRules } = props.army;

  if (armyRules.length === 0) {
    return null;
  }

  return (
    <section className={printable.section}>
      <Heading className={printable.sectionHeading}>
        Keywords & Abilities
      </Heading>

      <div className={rules.columns}>
        {armyRules.map((rule) => {
          return (
            <p key={rule.id} className={rules.rule}>
              <span className={rules.name}>{rule.name}</span> ·{" "}
              {rule.description}
            </p>
          );
        })}
      </div>
    </section>
  );
}

function ArmyPrintable(props: {
  army: Army;
  settings: PrintSettings;
  shareLinkOp: AsyncOp<string, string>;
}) {
  const { army, settings } = props;

  return (
    <div className={printable.paper}>
      <ArmyHeader {...props} />

      <HeadingLevel>
        <UnitsTable army={army} />

        {settings.showRules && <RulesReference army={army} />}
      </HeadingLevel>
    </div>
  );
}

function PrintPanel(props: {
  army: Army;
  settings: PrintSettings;
  onChange: (settings: Partial<PrintSettings>) => void;
  canPrint: boolean;
}) {
  const { army, settings, onChange, canPrint } = props;

  return (
    <div className={panel.container}>
      <div className={panel.header}>
        <IconButton
          href={`~/army/a/${army.data.id}`}
          icon="cross"
          label="Close"
        />
        <Icon name="divider" />
        <div className={panel.heading}>Print</div>
      </div>

      <HeadingLevel>
        <div className={panel.content}>
          <div>
            <Heading className={panel.subheading}>Options</Heading>

            <div className={panel.options}>
              <ToggleField
                label="Show keywords & abilities"
                checked={settings.showRules}
                onChange={(event) => {
                  onChange({ showRules: event.target.checked });
                }}
              />

              <ToggleField
                label="Show army description"
                checked={settings.showDescription}
                onChange={(event) => {
                  onChange({ showDescription: event.target.checked });
                }}
              />
            </div>
          </div>

          <div>
            <Heading className={panel.subheading}>Tips & Tricks</Heading>

            <p className={panel.text}>
              <strong>Use Chrome, Edge, or Firefox browser.</strong> Safari does
              not reproduce the page as it was designed.
            </p>

            <p className={panel.text}>
              <strong>Prefer printing on desktop.</strong> It is possible to use
              a phone/tablet, but the output is usually worse.
            </p>

            <p className={panel.text}>
              <strong>Use system dialog to fine-tune your print.</strong> You
              should be able to change <em>font size</em> with scale, set{" "}
              <em>margins</em>, and switch off <em>headers</em>.
            </p>

            <p className={panel.text}>
              <strong>Use Print to PDF</strong> to get a digital version of your
              printout.
            </p>
          </div>
        </div>
      </HeadingLevel>

      <div className={panel.footer}>
        <button
          type="button"
          disabled={!canPrint}
          className={btn({ sticky: false, textAlign: "center" })}
          onClick={() => {
            window.print();
          }}
        >
          Print
        </button>
      </div>
    </div>
  );
}

function PrintArmyContent(props: { army: Army }) {
  const { army } = props;
  const [settings, dispatch] = usePrintSettings();
  const shareLinkOp = useEnsureShareLink(army);
  useDocumentTheme(army.theme);

  return (
    <HeadingLevel>
      <div className={page.container}>
        <main className={page.preview}>
          <ArmyPrintable
            army={army}
            settings={settings}
            shareLinkOp={shareLinkOp}
          />
        </main>

        <PrintPanel
          army={army}
          settings={settings}
          onChange={dispatch}
          // Wait for the share link so that the QR code makes it into print
          canPrint={!shareLinkOp.isPending}
        />
      </div>
    </HeadingLevel>
  );
}

export function PrintArmyPage() {
  const armyOp = useArmyQuery();

  if (armyOp.isPending) {
    return <LoadingView />;
  }

  if (armyOp.isFailure) {
    return <KnownErrorView code={armyOp.failure.type} />;
  }

  return <PrintArmyContent army={armyOp.value} />;
}
